import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation } from 'react-router';

import { readReturnTarget, returnKind, returnStateFrom } from '@/shared/lib/return-target';
import { Icons } from '@/shared/ui';

import styles from './SettingsShell.module.css';

/**
 * Frame of every settings page: a "Return to the card / board" link when Settings was opened from
 * one, the section list (Profile, Personalization, Security) and the page. The sections link with
 * the same return state, so the way back survives moving between them.
 */
export function SettingsShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const location = useLocation();
  const target = readReturnTarget(location);
  const state = returnStateFrom(location);
  const kind = target ? returnKind(target) : null;
  const sections = [
    { to: '/app/account', label: t('settings.profile'), end: true, icon: <Icons.user /> },
    {
      to: '/app/settings/interface',
      label: t('settings.personalization'),
      end: false,
      icon: <Icons.palette />,
    },
    {
      to: '/app/settings/shortcuts',
      label: t('settings.shortcuts'),
      end: false,
      icon: <Icons.keyboard />,
    },
    { to: '/app/account/security', label: t('settings.security'), end: false, icon: <Icons.eye /> },
  ];
  return (
    <div className={styles.shell}>
      {target && kind && (
        <Link to={target} className={styles.return} data-testid="return-link">
          <Icons.back />
          {t(
            kind === 'card'
              ? 'settings.returnToCard'
              : kind === 'board'
                ? 'settings.returnToBoard'
                : 'settings.returnToWorkspace',
          )}
        </Link>
      )}
      <div className={styles.body}>
        <nav className={styles.nav} aria-label={t('settings.nav')}>
          {sections.map((section) => (
            <NavLink
              key={section.to}
              to={section.to}
              end={section.end}
              {...(state && { state })}
              className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
            >
              <span className={styles.linkIcon} aria-hidden>
                {section.icon}
              </span>
              {section.label}
            </NavLink>
          ))}
        </nav>
        <div className={styles.content}>
          <p className={styles.scope}>{t('settings.scope')}</p>
          {children}
        </div>
      </div>
    </div>
  );
}
